import { Container, Paper, Stack, Text, Title, Group, Badge, Button, Alert } from '@mantine/core'
import { useBranding } from '@/hooks/useBranding'
import { getCurrentDomain, setTestDomain, clearTestDomain } from '@/utils/domain'
import { Helmet } from 'react-helmet-async'
import { IconInfoCircle } from '@tabler/icons-react'

export const BrandingTestPage: React.FC = () => {
    const branding = useBranding()
    const domain = getCurrentDomain()

    return (
        <>
            <Helmet>
                <title>Branding Test | {branding.siteName}</title>
                <meta name="description" content="Test page to verify dynamic branding" />
            </Helmet>

            <Container size="md" py="xl">
                <Stack gap="xl">
                    <div>
                        <Title order={1} mb="md">
                            Dynamic Branding Test
                        </Title>
                        <Text c="dimmed">
                            This page shows the current branding information based on the domain.
                        </Text>
                    </div>

                    {/* Testing Controls */}
                    <Paper p="lg" radius="md" withBorder>
                        <Stack gap="md">
                            <Title order={3}>Testing Controls</Title>

                            <Alert icon={<IconInfoCircle size={16} />} color="blue">
                                <Text size="sm">
                                    <strong>Testing on localhost:</strong> Use these controls to test different domain configurations.
                                    You can also use URL parameters like <code>?test-domain=videogame.land</code> or set localStorage.
                                </Text>
                            </Alert>

                            <Group>
                                <Button
                                    onClick={() => setTestDomain('spellen.land')}
                                    variant="light"
                                    color="blue"
                                >
                                    Test Spellen.land
                                </Button>
                                <Button
                                    onClick={() => setTestDomain('videogame.land')}
                                    variant="light"
                                    color="green"
                                >
                                    Test Videogame.land
                                </Button>
                                <Button
                                    onClick={clearTestDomain}
                                    variant="light"
                                    color="gray"
                                >
                                    Clear Test Domain
                                </Button>
                            </Group>
                            <Group>
                                <Button
                                    onClick={() => {
                                        localStorage.setItem('test-domain', 'videogame.land')
                                        window.location.reload()
                                    }}
                                    variant="outline"
                                    color="green"
                                    size="sm"
                                >
                                    Set localStorage & Reload
                                </Button>
                                <Button
                                    onClick={() => {
                                        localStorage.removeItem('test-domain')
                                        window.location.reload()
                                    }}
                                    variant="outline"
                                    color="gray"
                                    size="sm"
                                >
                                    Clear localStorage & Reload
                                </Button>
                            </Group>

                            <Text size="sm" c="dimmed">
                                <strong>Quick URL testing:</strong>
                                <br />
                                • <a href="?test-domain=spellen.land">?test-domain=spellen.land</a>
                                <br />
                                • <a href="?test-domain=videogame.land">?test-domain=videogame.land</a>
                                <br />
                                • <a href="?">Clear URL parameters</a>
                            </Text>
                        </Stack>
                    </Paper>

                    <Paper p="lg" radius="md" withBorder>
                        <Stack gap="md">
                            <Group>
                                <Badge color="blue" size="lg">
                                    Current Domain
                                </Badge>
                                <Text fw={600}>{domain}</Text>
                                {domain !== window.location.hostname && (
                                    <Badge color="orange" size="sm">
                                        Test Mode
                                    </Badge>
                                )}
                            </Group>
                            <Group>
                                <Badge color="purple" size="lg">
                                    Actual Hostname
                                </Badge>
                                <Text fw={600}>{window.location.hostname}</Text>
                            </Group>
                            <Group>
                                <Badge color="green" size="lg">
                                    Browser Title
                                </Badge>
                                <Text fw={600}>{typeof document !== 'undefined' ? document.title : 'N/A'}</Text>
                            </Group>
                            <Group>
                                <Badge color="red" size="lg">
                                    Test Domain (localStorage)
                                </Badge>
                                <Text fw={600}>{localStorage.getItem('test-domain') || 'Not set'}</Text>
                            </Group>

                            <Group>
                                <Badge color="green" size="lg">
                                    Site Name
                                </Badge>
                                <Text fw={600}>{branding.siteName}</Text>
                            </Group>

                            <Group>
                                <Badge color="orange" size="lg">
                                    Site Name Short
                                </Badge>
                                <Text fw={600}>{branding.siteNameShort}</Text>
                            </Group>

                            <Group>
                                <Badge color="purple" size="lg">
                                    Title
                                </Badge>
                                <Text fw={600}>{branding.title}</Text>
                            </Group>

                            <Group>
                                <Badge color="red" size="lg">
                                    Description
                                </Badge>
                                <Text fw={600}>{branding.description}</Text>
                            </Group>

                            <Group>
                                <Badge color="teal" size="lg">
                                    Canonical URL
                                </Badge>
                                <Text fw={600}>{branding.canonicalUrl}</Text>
                            </Group>

                            <Group>
                                <Badge color="indigo" size="lg">
                                    OG Title
                                </Badge>
                                <Text fw={600}>{branding.ogTitle}</Text>
                            </Group>

                            <Group>
                                <Badge color="cyan" size="lg">
                                    OG Description
                                </Badge>
                                <Text fw={600}>{branding.ogDescription}</Text>
                            </Group>

                            <Group>
                                <Badge color="pink" size="lg">
                                    Twitter Title
                                </Badge>
                                <Text fw={600}>{branding.twitterTitle}</Text>
                            </Group>

                            <Group>
                                <Badge color="grape" size="lg">
                                    Twitter Description
                                </Badge>
                                <Text fw={600}>{branding.twitterDescription}</Text>
                            </Group>

                            <Group>
                                <Badge color="lime" size="lg">
                                    Author
                                </Badge>
                                <Text fw={600}>{branding.author}</Text>
                            </Group>

                            <Group>
                                <Badge color="yellow" size="lg">
                                    Apple Mobile Web App Title
                                </Badge>
                                <Text fw={600}>{branding.appleMobileWebAppTitle}</Text>
                            </Group>
                        </Stack>
                    </Paper>

                    <Paper p="lg" radius="md" withBorder>
                        <Stack gap="md">
                            <Title order={3}>Taglines by Country</Title>
                            <Group>
                                <Badge color="blue" size="lg">
                                    NL
                                </Badge>
                                <Text fw={600}>{branding.getTaglineForCountry('NL')}</Text>
                            </Group>
                            <Group>
                                <Badge color="green" size="lg">
                                    BE
                                </Badge>
                                <Text fw={600}>{branding.getTaglineForCountry('BE')}</Text>
                            </Group>
                            <Group>
                                <Badge color="orange" size="lg">
                                    US
                                </Badge>
                                <Text fw={600}>{branding.getTaglineForCountry('US')}</Text>
                            </Group>
                        </Stack>
                    </Paper>

                    <Paper p="lg" radius="md" withBorder>
                        <Stack gap="md">
                            <Title order={3}>Asset Paths</Title>
                            <Group>
                                <Badge color="blue" size="lg">
                                    Logo White
                                </Badge>
                                <Text fw={600}>{branding.logo.white}</Text>
                            </Group>
                            <Group>
                                <Badge color="green" size="lg">
                                    Logo Yellow
                                </Badge>
                                <Text fw={600}>{branding.logo.yellow}</Text>
                            </Group>
                            <Group>
                                <Badge color="orange" size="lg">
                                    Favicon ICO
                                </Badge>
                                <Text fw={600}>{branding.favicon.ico}</Text>
                            </Group>
                            <Group>
                                <Badge color="purple" size="lg">
                                    Favicon PNG 32
                                </Badge>
                                <Text fw={600}>{branding.favicon.png32}</Text>
                            </Group>
                            <Group>
                                <Badge color="red" size="lg">
                                    Favicon PNG 512
                                </Badge>
                                <Text fw={600}>{branding.favicon.png512}</Text>
                            </Group>
                            <Group>
                                <Badge color="teal" size="lg">
                                    Apple Touch Icon
                                </Badge>
                                <Text fw={600}>{branding.favicon.appleTouchIcon}</Text>
                            </Group>
                        </Stack>
                    </Paper>
                </Stack>
            </Container>
        </>
    )
} 